// EntityLogs.tsx
import React, { useState, useEffect } from 'react';
import { FiFileText, FiChevronDown, FiChevronUp } from 'react-icons/fi';
import axiosInstance from '../libs/axios';

// Types
enum ActionType {
  CREATE = 'create',
  READ = 'read',
  UPDATE = 'update',
  DELETE = 'delete',
  LOGIN = 'login',
  LOGOUT = 'logout',
  REGISTER = 'register',
  PASSWORD_CHANGE = 'password_change',
  EMAIL_VERIFY = 'email_verify',
  EXPORT = 'export',
  IMPORT = 'import',
  UPLOAD = 'upload',
  DOWNLOAD = 'download',
  CUSTOM = 'custom'
}

interface Log {
  id: string | number;
  user_id: number;
  entity: string;
  entity_id?: string | number;
  action: ActionType;
  message?: string;
  prev_data?: Record<string, any> | null;
  modified_data?: Record<string, any> | null;
  meta_data?: {
    url?: string;
    method?: string;
    timestamp?: string;
    ip_address?: string;
    user_agent?: string;
    [key: string]: any;
  } | null;
  created_at: string;
  updated_at: string;
  user?: {
    id: number;
    first_name: string;
    last_name: string;
    role?: {
      id: number;
      name: string;
    };
  };
}

interface DataChange {
  key: string;
  prevValue: any;
  modValue: any;
}

interface EntityLogsProps {
  entity: string;
  entityId: string | number;
  className?: string;
  maxHeight?: string;
  showTitle?: boolean;
}

const EntityLogs: React.FC<EntityLogsProps> = ({ 
  entity, 
  entityId,
  className = '',
  maxHeight = 'max-h-[600px]',
  showTitle = true
}) => {
  const [logs, setLogs] = useState<Log[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [expandedLogs, setExpandedLogs] = useState<Set<string | number>>(new Set());

  useEffect(() => {
    fetchLogs();
  }, [entity, entityId]);

  const fetchLogs = async (): Promise<void> => {
    try {
      setLoading(true);
      setError(null);
      const response = await axiosInstance.get(`/logs/${entity}/${entityId}`);
      setLogs(response.data.data.logs || []);
    } catch (err) {
      console.error('Error fetching logs:', err);
      setError('Failed to load activity logs');
    } finally {
      setLoading(false);
    }
  };

  const toggleLogExpansion = (logId: string | number): void => {
    setExpandedLogs(prev => {
      const newSet = new Set(prev);
      if (newSet.has(logId)) {
        newSet.delete(logId);
      } else {
        newSet.add(logId);
      }
      return newSet;
    });
  };

  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getActionColor = (action: string): string => {
    const colors: Record<string, string> = {
      create: 'bg-green-100 text-green-800 border-green-200',
      read: 'bg-blue-100 text-blue-800 border-blue-200',
      update: 'bg-blue-100 text-blue-800 border-blue-200',
      delete: 'bg-red-100 text-red-800 border-red-200',
      login: 'bg-purple-100 text-purple-800 border-purple-200',
      logout: 'bg-gray-100 text-gray-800 border-gray-200',
      register: 'bg-green-100 text-green-800 border-green-200',
      password_change: 'bg-yellow-100 text-yellow-800 border-yellow-200',
      email_verify: 'bg-teal-100 text-teal-800 border-teal-200',
      export: 'bg-indigo-100 text-indigo-800 border-indigo-200',
      import: 'bg-indigo-100 text-indigo-800 border-indigo-200',
      upload: 'bg-cyan-100 text-cyan-800 border-cyan-200',
      download: 'bg-cyan-100 text-cyan-800 border-cyan-200',
      custom: 'bg-gray-100 text-gray-800 border-gray-200',
    };
    return colors[action] || 'bg-gray-100 text-gray-800 border-gray-200';
  };

  const renderDataComparison = (
    prevData: Record<string, any> | null,
    modifiedData: Record<string, any> | null
  ) => {
    if (!prevData || !modifiedData) return null;

    const changes: DataChange[] = [];
    const allKeys = new Set([...Object.keys(prevData), ...Object.keys(modifiedData)]);

    allKeys.forEach(key => {
      const prevValue = prevData[key];
      const modValue = modifiedData[key];
      
      if (JSON.stringify(prevValue) !== JSON.stringify(modValue)) {
        changes.push({ key, prevValue, modValue });
      }
    });

    if (changes.length === 0) return null;

    return (
      <div className="mt-3 space-y-2">
        <p className="text-xs font-semibold text-gray-700">Changes ({changes.length}):</p>
        <div className="space-y-1.5">
          {changes.map(({ key, prevValue, modValue }) => (
            <div key={key} className="bg-gray-50 p-2 rounded border border-gray-200">
              <p className="text-xs font-medium text-gray-700 mb-1">{key}:</p>
              <div className="grid grid-cols-2 gap-2 text-xs">
                <div className="bg-red-50 p-1.5 rounded border border-red-200">
                  <span className="text-red-700 font-medium">Previous:</span>{' '}
                  <span className="text-gray-900 break-all">
                    {prevValue === null || prevValue === undefined || prevValue === '' 
                      ? '(empty)' 
                      : typeof prevValue === 'object' 
                        ? JSON.stringify(prevValue) 
                        : String(prevValue)}
                  </span>
                </div>
                <div className="bg-green-50 p-1.5 rounded border border-green-200">
                  <span className="text-green-700 font-medium">New:</span>{' '}
                  <span className="text-gray-900 break-all">
                    {modValue === null || modValue === undefined || modValue === '' 
                      ? '(empty)' 
                      : typeof modValue === 'object' 
                        ? JSON.stringify(modValue) 
                        : String(modValue)}
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const renderJsonData = (data: Record<string, any> | null, title: string)=> {
    if (!data) return null;

    return (
      <details className="mt-3">
        <summary className="cursor-pointer text-xs font-medium text-gray-700 hover:text-gray-900">
          View {title}
        </summary>
        <div className="mt-2">
          <pre className="bg-gray-100 p-3 rounded overflow-x-auto text-xs font-mono max-h-64 overflow-y-auto">
            {JSON.stringify(data, null, 2)}
          </pre>
        </div>
      </details>
    );
  };

  if (loading && logs.length === 0) {
    return (
      <div className={`bg-white rounded shadow-sm border border-gray-200 p-8 ${className}`}>
        <div className="text-center">
          <div className="w-8 h-8 border-2 border-blue-800 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Loading activity logs...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={`bg-white rounded shadow-sm border border-red-200 p-8 ${className}`}>
        <div className="text-center">
          <FiFileText className="w-12 h-12 text-red-400 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 mb-2">Error Loading Logs</h3>
          <p className="text-red-600 mb-4">{error}</p>
          <button
            onClick={fetchLogs}
            className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  if (logs.length === 0) {
    return (
      <div className={`bg-white rounded shadow-sm border border-gray-200 p-8 ${className}`}>
        <div className="text-center">
          <FiFileText className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 mb-2">No Activity Logs</h3>
          <p className="text-gray-600">No activity logs found for this {entity}.</p>
        </div>
      </div>
    );
  }

  return (
    <div className={`bg-white rounded shadow-sm border border-gray-200 p-4 ${className}`}>
      {showTitle && (
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-base font-semibold text-gray-900">Activity Logs</h3>
          <span className="text-sm text-gray-600">
            Total: {logs.length} {logs.length === 1 ? 'entry' : 'entries'}
          </span>
        </div>
      )}

      <div className={`space-y-4 overflow-y-auto ${maxHeight}`}>
        {logs.map((log) => {
          const isExpanded = expandedLogs.has(log.id);
          
          return (
            <div
              key={log.id}
              className="border border-gray-200 rounded-lg p-4 hover:border-blue-300 transition-colors bg-white"
            >
              <div className="flex items-start justify-between mb-3">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2 flex-wrap">
                    <span className={`inline-flex items-center px-2 py-1 rounded text-xs font-medium border ${getActionColor(log.action)}`}>
                      {log.action.toUpperCase().replace(/_/g, ' ')}
                    </span>
                    <span className="text-xs font-medium text-gray-700">
                      {log.entity.replace(/_/g, ' ').toUpperCase()}
                    </span>
                    {log.entity_id && (
                      <span className="text-xs text-gray-500">
                        ID: {log.entity_id}
                      </span>
                    )}
                  </div>
                  {log.message && (
                    <p className="text-sm text-gray-900 mb-1">{log.message}</p>
                  )}
                  {log.user && (
                    <p className="text-xs text-gray-600">
                      by <span className="font-medium">{log.user.first_name} {log.user.last_name}</span>
                      {log.user.role && <span className="text-gray-500"> ({log.user.role.name})</span>}
                    </p>
                  )}
                </div>
                <div className="text-right flex flex-col items-end gap-2">
                  <p className="text-xs text-gray-600">{formatDate(log.created_at)}</p>
                  {(log.prev_data || log.modified_data || log.meta_data) && (
                    <button
                      onClick={() => toggleLogExpansion(log.id)}
                      className="text-xs text-blue-600 hover:text-blue-800 flex items-center gap-1"
                    >
                      {isExpanded ? (
                        <>
                          <FiChevronUp className="w-3 h-3" />
                          Hide Details
                        </>
                      ) : (
                        <>
                          <FiChevronDown className="w-3 h-3" />
                          Show Details
                        </>
                      )}
                    </button>
                  )}
                </div>
              </div>

              {isExpanded && (
                <>
                  {log.meta_data && (
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-3 text-xs">
                      {log.meta_data.method && (
                        <div className="bg-gray-50 p-2 rounded border border-gray-200">
                          <span className="font-medium text-gray-700">Method:</span>{' '}
                          <span className="font-mono text-gray-900">{log.meta_data.method}</span>
                        </div>
                      )}
                      {log.meta_data.url && (
                        <div className="bg-gray-50 p-2 rounded border border-gray-200 col-span-2">
                          <span className="font-medium text-gray-700">URL:</span>{' '}
                          <span className="font-mono text-gray-900 break-all">{log.meta_data.url}</span>
                        </div>
                      )}
                      {log.meta_data.ip_address && (
                        <div className="bg-gray-50 p-2 rounded border border-gray-200">
                          <span className="font-medium text-gray-700">IP:</span>{' '}
                          <span className="font-mono text-gray-900">{log.meta_data.ip_address}</span>
                        </div>
                      )}
                    </div>
                  )}

                  {log.action === ActionType.UPDATE && log.prev_data && log.modified_data && 
                    renderDataComparison(log.prev_data, log.modified_data)
                  }

                  {/* {log.action === ActionType.CREATE && log.modified_data && 
                    renderJsonData(log.modified_data, 'Created Data')
                  } */}

                  {log.action === ActionType.DELETE && log.prev_data && 
                    renderJsonData(log.prev_data, 'Deleted Data')
                  }

                  {/* Sowing MetaData */}
                  {/* {log.meta_data && (
                    <details className="mt-3 pt-3 border-t border-gray-200">
                      <summary className="cursor-pointer text-xs font-medium text-gray-700 hover:text-gray-900">
                        View Full Metadata
                      </summary>
                      <div className="mt-2">
                        <pre className="bg-gray-100 p-3 rounded overflow-x-auto text-xs font-mono">
                          {JSON.stringify(log.meta_data, null, 2)}
                        </pre>
                      </div>
                    </details>
                  )} */}
                </>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default EntityLogs;